// Phase 2 — Types for the new video Director Creative draft flow.
//
// These types mirror the Kotlin DTOs in ecommerce-service:
//   - CreateVideoDraftRequest.kt
//   - VideoDraftResponse.kt
//
// The flow:
//   1. POST /ads/videos/draft (CreateVideoDraftRequest) → server returns VideoDraftResponse
//      with status="directing" and a reference_id
//   2. Frontend polls GET /ads/videos/draft/{ref} every 5s
//   3. When status="ready_for_review", show the preview screen
//   4. User clicks Aprobar → POST /draft/{ref}/approve → status="ready_for_review" + approved_at
//   5. OR clicks Rechazar → POST /draft/{ref}/reject → status="rejected" + refund 100%

export interface CreateVideoDraftRequest {
  product_id?: string;
  style_id: string; // 'animated-problem' | 'sassy-object' | ...
  duration: number; // 5 | 10 | 15 | 30
  language: string;

  /** Optional sale angle, comes from a Narrative if it exists. */
  sale_angle_name?: string;
  sale_angle_description?: string;

  /** Optional target audience, sent by the frontend. */
  target_audience_description?: string;
  target_audience_vibe?: string;

  /** Optional free-text creative direction. */
  user_instruction?: string;

  /** Optional override of the agent_id (advanced). */
  agent_id_override?: string;
}

/**
 * Mirror of the Kotlin VideoDraftResponse.
 *
 * Status values (from VideoState enum):
 *   - "directing"          → director is running on conversation-engine
 *   - "media_generating"   → reserved for Phase 3
 *   - "ready_for_review"   → director_payload populated, ready for user
 *   - "approved"           → user approved (still ready_for_review state, approved_at != null)
 *   - "rejected"           → user rejected, refund 100%
 *   - "error"              → director failed, refund 100%
 *   - "rendering"          → Phase 3 (after approve, FAL is rendering)
 *   - "finished"           → Phase 3 (final video ready)
 */
export interface VideoDraftResponse {
  reference_id: string;
  status: string;
  style_id?: string;
  duration?: number;
  language?: string;
  product_id?: string;
  product_name?: string;
  product_image_url?: string;
  credits_charged?: number;
  director_payload?: VideoDirectorPayload;
  error_message?: string;
  /** Phase 5.2: imagen base generada post-approve. Se popula cuando state pasa a waiting_scenes. */
  base_image_url?: string | null;
  /** Phase 5.2: video final mergeado. Se popula cuando state pasa a finished. */
  video_url?: string | null;
  /** Phase 5.4: cuántas regeneraciones de imagen/estilo lleva este draft. */
  regenerate_count?: number;
  /** Phase 5.4: tope de regeneraciones permitidas (3). Cuando regenerate_count llega a este valor, los botones se deshabilitan. */
  max_regenerate_count?: number;
  created_at?: string;
  updated_at?: string;
  approved_at?: string;
  rejected_at?: string;
}

/**
 * Phase 5.4 follow-up: body opcional para POST /draft/{ref}/regenerate-image.
 *
 * Si se manda `instruction`, el backend lo APPENDEA al concept_visual_brief
 * existente como contexto adicional para el image generator. NO reemplaza
 * el wrapper Pixar character ni el resto del prompt — es solo una capa
 * extra de personalización opcional. Si no se manda nada, el endpoint se
 * comporta exactamente como antes (regen con el mismo brief).
 */
export interface RegenerateDraftImageRequest {
  instruction?: string | null;
}

/** Phase 5.4: body para POST /draft/{ref}/edit-script */
export interface EditDraftScriptRequest {
  script_part_a: string;
  script_part_b?: string | null;
}

/** Phase 5.4: body para POST /draft/{ref}/regenerate-style */
export interface RegenerateDraftStyleRequest {
  pattern_key?: string | null;
  /**
   * Optional natural-language hint for the director ("más enojado",
   * "menos dramático", "más sarcástico"). The backend passes it as
   * user_instruction to the LLM. If null/empty, the director picks
   * a different pattern at random.
   */
  instruction?: string | null;
}

/**
 * Mirror of the Pydantic VideoStudioDraftReadyPayload from conversation-engine.
 *
 * This is what the LLM director returns. The frontend reads it from
 * draft.director_payload to render the preview.
 */
export interface VideoDirectorPayload {
  selected_pattern_key: string;
  selection_reasoning: string;
  concept_visual_brief: string;
  script_part_a: string;
  script_part_b?: string | null;
  ends_with_product_name: boolean;
  cinematic_camera_a: string;
  cinematic_camera_b?: string | null;
  cinematic_prompt_a: string;
  cinematic_prompt_b?: string | null;
  viral_hook_first_3_seconds: string;
}

/** Frontend-only state machine of the draft for the UI. Mirror of VideoState.kt externalValue. */
export const VIDEO_DRAFT_STATUS = {
  DIRECTING: "directing",
  MEDIA_GENERATING: "media_generating",
  READY_FOR_REVIEW: "ready_for_review",
  REJECTED: "rejected",
  ERROR: "error",
  // Post-approve states (Phase 3 dispatch + legacy FAL pipeline)
  GENERATING_IMAGE: "generating_image",
  WAITING_SCENES: "waiting_scenes",
  MERGING: "merging",
  FINISHED: "finished",
} as const;

export type VideoDraftStatus = (typeof VIDEO_DRAFT_STATUS)[keyof typeof VIDEO_DRAFT_STATUS];
